/* eslint-disable no-restricted-imports */
import { useCallback } from 'react';
import { DirectSpendLimitType, DirectStrategyType } from '../../../../../data-layer/direct/types';
import { getIsMobile } from '../../../../../utils/getIsMobile';
import { useTranslation } from '../../../../../utils/useTranslation';
import { WithHint } from '../../../../feedback/Tooltip';
import { FormControl } from '../../../../input/FormControl';
import { Input as InputBase } from '../../../../input/Input';
import { makeTextControlValidate } from '../../../../input/makeTextControlValidate';
import { Box } from '../../../../layout/Box';
import { Flex } from '../../../../layout/Flex';
import { Price } from '../../../../price/Price';
import { Typography } from '../../../../typography/Typography';
import { getMinWeeklyLimit } from '../../utils/getMinWeeklyLimit';
import { ContentPromotionDropdown } from '../ContentPromotionDropdown/ContentPromotionDropdown';
import {
    crrValidator,
    weeklyLimitValidator,
    payForOptions,
    strategyOptions,
    showAverageCpcValidator,
} from './constants';
import { Field, FieldsWrapper, Wrapper } from './styles';
import type { ContentPromotionBudgetStrategyProps } from './types';

const Input = makeTextControlValidate(InputBase);

export const ContentPromotionBudgetStrategy: React.FC<ContentPromotionBudgetStrategyProps> = ({
    strategyType,
    payFor,
    budgetType,
    spendLimitType,
    spendLimitAmount,
    weeklyLimit,
    selectedGoals,
    spendLimitOptions,
    onChangeStrategyType: onChangeStrategyTypeBase,
    onChangePayFor,
    onChangeBudgetType,
    onChangeSpendLimitType,
    onChangeSpendLimitAmount,
    onChangeWeeklyLimit,
    onChangeDefaultGoal,
    ...other
}) => {
    const t = useTranslation();
    const { isMobile } = getIsMobile();

    const minBudget = getMinWeeklyLimit(selectedGoals, payFor);

    const showCrr = spendLimitType === DirectSpendLimitType.CRR;
    const showAverageCpc = spendLimitType === DirectSpendLimitType.AVERAGE_CPC;
    const showGoalInputDisclaimer =
        spendLimitType === DirectSpendLimitType.CONVERSION_COST ||
        spendLimitType === DirectSpendLimitType.AVERAGE_CPA;

    const onMinBudgetClick = useCallback(() => {
        onChangeWeeklyLimit(String(minBudget));
    }, [minBudget, onChangeWeeklyLimit]);

    const onChangeStrategyType = useCallback(
        (value: string) => {
            onChangeStrategyTypeBase(value as DirectStrategyType);

            onChangeDefaultGoal(value === DirectStrategyType.MAX_CLICKS);
        },
        [onChangeStrategyTypeBase, onChangeDefaultGoal],
    );

    return (
        <Wrapper {...other}>
            <Flex direction="row" alignItems="center" pb={1}>
                <Typography variant="body" weight="bold">
                    {t('Стратегия')}
                </Typography>
                <WithHint
                    ml={1}
                    iconColor="var(--color-secondary)"
                    message={t(
                        'Стратегия — это набор настроек, которые позволяют решить задачу кампании, например привлечь трафик на сайт, получить максимум полезных действий, оптимизировать рентабельность или увеличить прибыль от продвижения.',
                    )}
                />
            </Flex>

            <FieldsWrapper isVertical={isMobile}>
                <Field isVertical={isMobile}>
                    <ContentPromotionDropdown
                        label={t('Стратегия')}
                        value={strategyType}
                        options={strategyOptions}
                        onChange={onChangeStrategyType}
                    />
                </Field>
                <Field isVertical={isMobile}>
                    <ContentPromotionDropdown
                        label={t('С оплатой')}
                        value={payFor}
                        options={payForOptions}
                        onChange={(value) => onChangePayFor(value as any)}
                        disabled={strategyType === DirectStrategyType.MAX_CLICKS}
                    />
                </Field>
            </FieldsWrapper>

            <FieldsWrapper isVertical={isMobile}>
                <Field isVertical={isMobile} width="100%">
                    <FormControl label={t('Недельный бюджет')} width="100%">
                        <>
                            <Input
                                variant="outline"
                                placeholder={t('Сумма в ₽')}
                                value={weeklyLimit ? weeklyLimit.toString() : undefined}
                                onChange={(e) =>
                                    onChangeWeeklyLimit((e.target as HTMLInputElement).value)
                                }
                                min={0}
                                step={1}
                                type="number"
                                validator={weeklyLimitValidator}
                            />
                            {minBudget ? (
                                <Flex direction="row" mt={1}>
                                    <Typography variant="body2">
                                        {t('Минимальный рекомендуемый бюджет')}
                                    </Typography>
                                    <Box onClick={onMinBudgetClick} ml={0.5} cursor="pointer">
                                        <Price
                                            priceInfo={{
                                                integerPart: minBudget,
                                                currency: 'RUB',
                                            }}
                                            variant="body2"
                                            weight="regular"
                                            color="link"
                                        />
                                    </Box>
                                </Flex>
                            ) : null}
                        </>
                    </FormControl>
                </Field>
            </FieldsWrapper>

            <FieldsWrapper isVertical={isMobile}>
                <Field width="100%">
                    <ContentPromotionDropdown
                        label={t('Ограничение расхода')}
                        value={spendLimitType}
                        options={spendLimitOptions}
                        onChange={(value) => onChangeSpendLimitType(value as any)}
                    />
                </Field>
            </FieldsWrapper>
            <FieldsWrapper isVertical={isMobile}>
                {showCrr ? (
                    <Field width="100%" position="relative">
                        <FormControl label={t('Доля рекламных расходов')} width="100%">
                            <Input
                                variant="outline"
                                placeholder={t('Доля рекламных расходов')}
                                value={spendLimitAmount ? spendLimitAmount.toString() : undefined}
                                onChange={(e) =>
                                    onChangeSpendLimitAmount((e.target as HTMLInputElement).value)
                                }
                                step={1}
                                min={0}
                                max={'500'}
                                type="number"
                                validator={crrValidator}
                            />
                        </FormControl>
                        <Typography color="secondary" position="absolute" right={2} top={5}>
                            {'%'}
                        </Typography>
                    </Field>
                ) : null}
                {showAverageCpc ? (
                    <Field width="100%" position="relative">
                        <FormControl label={t('Средняя цена клика')} width="100%">
                            <Input
                                variant="outline"
                                placeholder={t('Средняя цена клика')}
                                value={spendLimitAmount ? spendLimitAmount.toString() : undefined}
                                onChange={(e) =>
                                    onChangeSpendLimitAmount((e.target as HTMLInputElement).value)
                                }
                                min={0}
                                step={1}
                                type="number"
                                validator={showAverageCpcValidator}
                            />
                        </FormControl>
                        <Typography color="secondary" position="absolute" right={2} top={5}>
                            {'₽'}
                        </Typography>
                    </Field>
                ) : null}
                {showGoalInputDisclaimer ? (
                    <Flex pt={0.5} width="100%" alignItems="center">
                        <Typography color="secondary">
                            {t('Поле для ввода будет рядом с целью')}
                        </Typography>
                    </Flex>
                ) : null}
            </FieldsWrapper>
        </Wrapper>
    );
};
